import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import MSTRMobileChartWrapper from './MSTRMobileChartWrapper';
import MobileInfluencerFilter from '../MobileInfluencerFilter';
import { Prediction } from '../../../types/predictions';
import { Theme } from '../../../theme';
import { formatPrediction } from '../../../utils/priceUtils';
import { formatDate } from '../../../utils/dateUtils';

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  height: 100vh;
  padding: 8px 12px 8px 8px; /* More padding on the right side */
  box-sizing: border-box; /* Ensure padding is included in width */
  overflow-x: hidden; /* Prevent horizontal overflow */
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 400px;
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border}; /* Add missing border */
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box; /* Include border in width calculation */
`;

const PredictionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  width: 100%; /* Ensure full width */
  box-sizing: border-box; /* Proper box model */
`;

const PredictionCard = styled.div<{ $active: boolean }>`
  padding: 12px;
  background: ${props => props.$active ? props.theme.primary + '20' : props.theme.surface};
  border: 1px solid ${props => props.$active ? props.theme.primary : props.theme.border}; /* Ensure border always shows */
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%; /* Full width */
  box-sizing: border-box; /* Include padding/border in width */

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.$active ? props.theme.primary + '30' : props.theme.hoverBackground}; /* Add hover background */
  }
`;

const ExpertName = styled.div`
  font-weight: bold;
  color: ${props => props.theme.primary};
  margin-bottom: 4px;
`;

const PredictionText = styled.div`
  color: ${props => props.theme.text};
  font-size: 14px;
  margin-bottom: 4px;
`;

const TargetTime = styled.div`
  color: ${props => props.theme.subText};
  font-size: 12px;
`;

const PredictionTypeIcon = styled.span`
  margin-right: 4px;
  font-size: 12px;
`;

// Submit card styled components
const MSTRSubmitCard = styled.div`
  padding: 16px;
  background: linear-gradient(135deg, ${props => props.theme.primary}12, ${props => props.theme.primary}20);
  border: 2px dashed ${props => props.theme.primary}50;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  margin-bottom: 8px;
  
  &:active {
    transform: scale(0.98);
    background: linear-gradient(135deg, ${props => props.theme.primary}20, ${props => props.theme.primary}30);
  }
`;

const MSTRSubmitCardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const MSTRSubmitCardIcon = styled.div`
  font-size: 28px;
  margin-bottom: 4px;
`;

const MSTRSubmitCardTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: ${props => props.theme.primary};
  margin-bottom: 2px;
`;

const MSTRSubmitCardSubtitle = styled.div`
  font-size: 12px;
  color: ${props => props.theme.subText};
  opacity: 0.9;
`;

interface MSTRMobileMainChartPageProps {
  activePredictions: number[];
  togglePrediction: (id: number) => void;
  chartData: Array<{ time: string; value: number }>; // Ignored for MSTR
  isDarkMode: boolean;
  theme: Theme;
  predictions: Prediction[]; // These are Bitcoin predictions, we'll fetch MSTR separately
  openModal?: () => void; // Optional modal opener for submit functionality
}

const MSTRMobileMainChartPage: React.FC<MSTRMobileMainChartPageProps> = ({
  activePredictions,
  togglePrediction,
  chartData,
  isDarkMode,
  theme,
  predictions,
  openModal
}) => {
  const location = useLocation();
  const [mstrPredictions, setMstrPredictions] = useState<Prediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState<string>('all');
  
  // Fetch MSTR-specific predictions
  useEffect(() => {
    const fetchMSTRPredictions = async () => {
      setIsLoading(true);
      try {
        const apiUrl = '/api/mstr/predictions';
        
        
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success && result.data) {
          // Transform MSTR predictions to match Bitcoin prediction format
          const transformedPredictions = result.data.map((item: any) => ({
            id: item.id,
            influencer_id: item.influencer_id, // FIX: use influencer_id from backend
            price_prediction: item.price_prediction,
            target_time: item.target_time,
            success_status: item.success_status || 'pending',
            source_url: item.source_url,
            quote: item.notes,
            vote_score: 0,
            created_at: item.created_at,
            start_date: item.start_date || item.created_at?.split('T')[0],
            end_date: item.end_date || item.target_time,
            price_min: item.price_min,
            price_max: item.price_max,
            price_target: item.target_price, // Use target_price from MSTR API
            exclude_from_chart: false,
            prediction_type: item.prediction_type || 'target',
            influencer: {
              id: item.influencer_id, // FIX: use influencer_id
              name: item.influencer_name || 'Unknown Expert', // FIX: use influencer_name
              score: item.influencer_score || 0
            }
          }));
          
          setMstrPredictions(transformedPredictions);
        } else {
          setMstrPredictions([]);
        }
      } catch (error) {
        console.error(`📱 MSTR Mobile: Error fetching MSTR predictions:`, error);
        setMstrPredictions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMSTRPredictions();
  }, []);

  const handlePredictionClick = (predictionId: number) => {
    togglePrediction(predictionId);
  };

  // Handle influencer filter change
  const handleInfluencerChange = (influencer: string) => {
    setSelectedInfluencer(influencer);
  };

  // Extract unique influencers
  const uniqueInfluencers = React.useMemo(() => {
    const influencers = new Set<string>();
    mstrPredictions.forEach(pred => {
      if (pred.influencer?.name) {
        influencers.add(pred.influencer.name);
      }
    });
    return Array.from(influencers).sort();
  }, [mstrPredictions]);

  // Filter predictions based on influencer
  const filteredMstrPredictions = React.useMemo(() => {
    if (selectedInfluencer === 'all') return mstrPredictions;
    
    return mstrPredictions.filter(prediction => 
      prediction.influencer?.name === selectedInfluencer
    );
  }, [mstrPredictions, selectedInfluencer]);

  if (isLoading) {
    return (
      <MobileContainer>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '200px',
          color: theme.text 
        }}>
          Loading MSTR predictions...
        </div>
      </MobileContainer>
    );
  }

  return (
    <MobileContainer>
      <ChartContainer>
        <MSTRMobileChartWrapper
          predictions={mstrPredictions} // Use MSTR predictions
          activePredictions={activePredictions}
          chartData={chartData} // This will be ignored by MSTRMobileChartWrapper
          isDarkMode={isDarkMode}
          theme={theme}
        />
      </ChartContainer>
      
      <MobileInfluencerFilter
        theme={theme}
        selectedInfluencer={selectedInfluencer}
        uniqueInfluencers={uniqueInfluencers}
        onInfluencerChange={handleInfluencerChange}
        filteredCount={filteredMstrPredictions.length}
        totalCount={mstrPredictions.length}
      />
      
      <PredictionsList>
        {openModal && (
          <MSTRSubmitCard onClick={openModal}>
            <MSTRSubmitCardContent>
              <MSTRSubmitCardIcon>📈</MSTRSubmitCardIcon>
              <MSTRSubmitCardTitle>Submit MicroStrategy Prediction</MSTRSubmitCardTitle>
              <MSTRSubmitCardSubtitle>Share your MSTR prediction with the community</MSTRSubmitCardSubtitle>
            </MSTRSubmitCardContent>
          </MSTRSubmitCard>
        )}
        {mstrPredictions.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: theme.subText 
          }}>
            <div style={{ marginBottom: '10px' }}>
              No MSTR predictions yet
            </div>
            <div style={{ fontSize: '14px' }}>
              Be the first to submit a MicroStrategy prediction!
            </div>
          </div>
        ) : (
          filteredMstrPredictions.map((prediction) => (
            <PredictionCard
              key={prediction.id}
              $active={activePredictions.includes(prediction.id)}
              onClick={() => handlePredictionClick(prediction.id)}
            >
              <ExpertName>{prediction.influencer?.name || 'Unknown Expert'}</ExpertName>
              <PredictionText>{formatPrediction(prediction)}</PredictionText>
              <TargetTime>
                <PredictionTypeIcon>
                  {prediction.prediction_type === 'target' ? '🎯' : 
                   prediction.prediction_type === 'top' ? '🔝' :
                   prediction.prediction_type === 'bottom' ? '📉' :
                   prediction.prediction_type === 'pricetimerange' ? '⏱️' :
                   prediction.prediction_type === 'bet' ? '🎲' :
                   prediction.prediction_type === 'sentiment' ? '💬' :
                   '📊'}
                </PredictionTypeIcon>
                {prediction.prediction_type === 'sentiment' ? (
                  // For sentiment predictions, only show when it was made (no target date)
                  prediction.prediction_made && `📅 Made: ${formatDate(prediction.prediction_made, 'dd/MM/yyyy')}`
                ) : (
                  // For price predictions, show target date
                  <>
                    {prediction.prediction_type === 'target' ? 'Target: ' : 
                     prediction.prediction_type === 'top' ? 'Top: ' :
                     prediction.prediction_type === 'bottom' ? 'Bottom: ' :
                     prediction.prediction_type === 'pricetimerange' ? 'Price by: ' :
                     prediction.prediction_type === 'bet' ? (
                       prediction.end_date && new Date(prediction.end_date) < new Date() ? 'Finished: ' : 'Due date: '
                     ) :
                     'Range: '}
                    {prediction.target_time ? formatDate(prediction.target_time, 'dd/MM/yyyy') : prediction.end_date ? formatDate(prediction.end_date, 'dd/MM/yyyy') : 'Date not specified'}
                  </>
                )}
              </TargetTime>
            </PredictionCard>
          ))
        )}
      </PredictionsList>
    </MobileContainer>
  );
};

export default MSTRMobileMainChartPage;
